/**
 * Which delivery line a scanned bag belongs on, when a product has more than one.
 *
 * The dispatch counterpart of `receipt-lines.ts`. A delivery can list the same product on
 * several lines when it is typed straight into Odoo (Feed Manager refuses a product twice on a
 * feed order). The screen used to keep one running count per product, so:
 *
 *  - the progress total added that product count once per line, so one bag read as 2;
 *  - every bag carried the Worker's move, which is the product's first line, so the first line
 *    read over-picked and the second stayed at 0;
 *  - the per-batch cap only looked at the first line's reservation, so a batch reserved on the
 *    second line was never capped.
 *
 * Unlike a receipt, a delivery is reserved: Odoo has put particular batches on particular lines,
 * and the Worker's commit only swaps one bag for another of the same batch **within the bag's
 * line** (`commit.ts` step 1b). So a bag goes first to a line that reserved its batch and still
 * has room for it. Only a batch no line reserved falls back to the receipt rule — lines in Odoo
 * order, each up to its expected quantity — and anything beyond that lands on the last line,
 * where the over-dispatch warning already applies.
 */

import { batchOfLotName } from "./batch.ts";

/** The fields of a picking move this needs. */
export interface DispatchLine {
  id: number;
  product: { id: number };
  expectedQty: number;
  doneQty: number;
  reservedLots?: readonly { lotName: string; reservedQty: number }[];
}

/** The fields of a bag already scanned this session that this needs. */
export interface DispatchedBag {
  moveId: number;
  productId: number;
  batch: string;
  quantity: number;
}

/** A share of a scanned quantity, on one line. */
export interface LineShare {
  moveId: number;
  quantity: number;
}

/** How many bags of this batch Odoo reserved on this line. */
export function reservedOnLine(line: DispatchLine, batch: string): number {
  return (line.reservedLots ?? [])
    .filter((lot) => batchOfLotName(lot.lotName) === batch)
    .reduce((sum, lot) => sum + lot.reservedQty, 0);
}

/** This session's quantity on one line, optionally for one batch. */
function scannedOnLine(bags: readonly DispatchedBag[], moveId: number, batch?: string): number {
  return bags
    .filter((bag) => bag.moveId === moveId && (batch === undefined || bag.batch === batch))
    .reduce((sum, bag) => sum + bag.quantity, 0);
}

/**
 * Split a scanned quantity of one batch across the product's lines.
 *
 * `bags` is everything already scanned this session, each carrying the line it was put on. A
 * serial bag is a quantity of 1 and lands whole on one line; a counted lot entry can span lines.
 * Returns an empty list when the product is not on the delivery, so the caller keeps the
 * Worker's move.
 */
export function allocateDispatch(
  lines: readonly DispatchLine[],
  bags: readonly DispatchedBag[],
  productId: number,
  batch: string,
  quantity: number,
): LineShare[] {
  const own = lines.filter((line) => line.product.id === productId);
  const last = own[own.length - 1];
  if (last === undefined || quantity <= 0) {
    return [];
  }

  const shares = new Map<number, number>();
  let left = quantity;
  const take = (line: DispatchLine, room: number): void => {
    if (left <= 0 || room <= 0) return;
    const n = Math.min(room, left);
    shares.set(line.id, (shares.get(line.id) ?? 0) + n);
    left -= n;
  };

  // 1. Lines that reserved this batch, up to what they reserved of it.
  const reserving = own.filter((line) => reservedOnLine(line, batch) > 0);
  for (const line of reserving) {
    take(
      line,
      reservedOnLine(line, batch) -
        scannedOnLine(bags, line.id, batch) -
        (shares.get(line.id) ?? 0),
    );
  }

  // 2. A batch nobody reserved: fill lines in Odoo order, up to each one's expected quantity.
  if (reserving.length === 0) {
    for (const line of own) {
      take(
        line,
        line.expectedQty - line.doneQty - scannedOnLine(bags, line.id) - (shares.get(line.id) ?? 0),
      );
    }
  }

  // 3. Beyond that is an over-dispatch: kept on the last line that reserved this batch, so the
  //    Worker can still swap it into that line's reservation, or else the product's last line.
  if (left > 0) {
    const home = reserving[reserving.length - 1] ?? last;
    shares.set(home.id, (shares.get(home.id) ?? 0) + left);
  }

  return own
    .filter((line) => shares.has(line.id))
    .map((line) => ({ moveId: line.id, quantity: shares.get(line.id) ?? 0 }));
}

/**
 * The product's expected and picked quantities across all its lines, for the over-dispatch
 * check. Comparing against one line's expected quantity would warn "extra bag" while the next
 * line still wants bags.
 */
export function productTotals(
  lines: readonly DispatchLine[],
  bags: readonly DispatchedBag[],
  productId: number,
): { expected: number; picked: number } {
  let expected = 0;
  let picked = 0;
  for (const line of lines) {
    if (line.product.id !== productId) continue;
    expected += line.expectedQty;
    picked += line.doneQty + scannedOnLine(bags, line.id);
  }
  return { expected, picked };
}

/**
 * How many bags of a batch Odoo reserved for the product across all its lines, and how many
 * this session has scanned. The per-batch cap: once a batch's reservation is met, the operator
 * is sent to another batch rather than over-shipping one and under-shipping another.
 */
export function batchTotals(
  lines: readonly DispatchLine[],
  bags: readonly DispatchedBag[],
  productId: number,
  batch: string,
): { reserved: number; scanned: number } {
  const reserved = lines
    .filter((line) => line.product.id === productId)
    .reduce((sum, line) => sum + reservedOnLine(line, batch), 0);
  const scanned = bags
    .filter((bag) => bag.productId === productId && bag.batch === batch)
    .reduce((sum, bag) => sum + bag.quantity, 0);
  return { reserved, scanned };
}
